import { onPageIn, onMount, onDestroy } from "@/modules/_";
import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function (element: HTMLElement, dataset: DOMStringMap) {
  let swiperInstances: Swiper[] = [];

  const formatImage = async (): Promise<void> => {
    return new Promise((resolve) => {
      document
        .querySelectorAll<HTMLElement>("[data-aspect-ratio]")
        .forEach((el) => (el.style.aspectRatio = el.dataset.aspectRatio!));

      document
        .querySelectorAll<HTMLElement>("[data-background]")
        .forEach((el) => {
          const bg = el.dataset.background!.trim();

          if (
            bg.startsWith("linear-gradient") ||
            bg.startsWith("radial-gradient") ||
            bg.startsWith("conic-gradient") ||
            bg.startsWith("url")
          ) {
            el.style.backgroundImage = bg;
          } else {
            el.style.backgroundColor = bg;
          }
        });

      document
        .querySelectorAll<HTMLElement>("[data-text-color]")
        .forEach((el) => (el.style.color = el.dataset.textColor!));

      requestAnimationFrame(() => resolve());
    });
  };

  const manageAllSlider = (): void => {
    const allSliders = document.querySelectorAll<HTMLElement>("[data-slider]");

    allSliders.forEach((slider) => {
      const sliderType = slider.getAttribute("data-slider") as "main" | "small";

      const nextBtn = slider.querySelector<HTMLElement>("[data-slider-next]");
      const prevBtn = slider.querySelector<HTMLElement>("[data-slider-prev]");
      const pagination = slider.querySelector<HTMLElement>(
        "[data-slider-pagination]"
      );
      const swiperEl = slider.querySelector<HTMLElement>(".swiper");

      if (!swiperEl) return;

      const configs: Record<string, SwiperOptions> = {
        main: {
          loop: true,
          modules: [Navigation, Pagination],
          spaceBetween: 40,
          slidesPerView: 1.3,
          centeredSlides: true,
          speed: 800,
          grabCursor: true,
          parallax: true,
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn,
          },
          pagination: {
            el: pagination,
            clickable: true,
            bulletClass: "slider_pagination_bullet",
            bulletActiveClass: "is--active",
          },
          breakpoints: {
            320: {
              slidesPerView: 1,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 1.3,
              spaceBetween: 25,
            },
            1024: {
              slidesPerView: 1.3,
              spaceBetween: 40,
            },
          },
        },
        small: {
          loop: true,
          modules: [Navigation, Pagination],
          spaceBetween: 0,
          slidesPerView: 3,
          centeredSlides: true,
          initialSlide: 0,
          speed: 800,
          grabCursor: true,
          parallax: true,
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn,
          },
          pagination: {
            el: pagination,
            clickable: true,
            bulletClass: "small_slider_pagination_bullet",
            bulletActiveClass: "is--active",
          },
          breakpoints: {
            320: {
              slidesPerView: 2,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 2.3,
              spaceBetween: 25,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 40,
            },
          },
        },
      };

      const config = configs[sliderType];

      if (!config) {
        console.warn(`Unknown slider type: ${sliderType}`);
        return;
      }

      const swiperInstance = new Swiper(swiperEl, config);
      swiperInstances.push(swiperInstance);
    });
  };

  onMount(async () => {
    await formatImage();

    manageAllSlider();
  });

  onDestroy(() => {
    swiperInstances.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    swiperInstances = [];
  });
}
